const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

export const FINANCIAL_AID_ACCESS_FIELD = 'financialAidEnabled' as const;
export const FINANCIAL_AID_ACCESS_DECISION_FIELD = 'financialAidAccessDecision' as const;
export const FINANCIAL_AID_ACCESS_DECISION_ENABLED = 'enabled' as const;
export const FINANCIAL_AID_ACCESS_DECISION_REVOKED = 'revoked' as const;

export const PARENT_FINANCIAL_AID_ACCESS_ROUTE_PREFIXES = [
  '/parent/portal/financialaid',
  '/parent/portal/financial-aid',
  '/parent/portal--new/financialaid',
  '/parent/portal--new/financial-aid',
  '/parent/financialaid',
  '/parent/financial-aid',
  '/portal/family/financial-aid',
  '/portal/family/financialaid',
] as const;

export const PARENT_FINANCIAL_AID_FOCUSED_NAV_IDS = [
  'dashboard',
  'financial-aid',
] as const;

export type ParentFinancialAidShellFocusInput = {
  isParentRoute: boolean;
  entryFocusActive?: boolean | null;
  inviteEntryActive?: boolean | null;
  cycleOpen?: boolean | null;
  openCycleEntryEnabled?: boolean | null;
  openCycleLookupPending?: boolean | null;
};

/**
 * Keeps explicit Financial Aid entry focus separate from general access.
 *
 * The open-cycle fields are accepted deliberately so regression tests can
 * prove that availability and lookup state never narrow the parent shell.
 */
export const shouldFocusParentShellOnFinancialAid = (
  input: ParentFinancialAidShellFocusInput,
): boolean => Boolean(
  input.isParentRoute
    && (input.entryFocusActive || input.inviteEntryActive),
);

export const resolveParentFinancialAidFocusedNavIds = (
  enabledNavIds: ReadonlySet<string>,
  focusActive: boolean,
  options: {
    includeDashboard?: boolean;
    includeFinancialAid?: boolean;
  } = {},
): Set<string> => {
  if (!focusActive) {
    return new Set(enabledNavIds);
  }
  const focusedEnabledNavIds = new Set(enabledNavIds);
  if (options.includeDashboard) {
    focusedEnabledNavIds.add('dashboard');
  }
  if (options.includeFinancialAid) {
    focusedEnabledNavIds.add('financial-aid');
  }
  return new Set(
    PARENT_FINANCIAL_AID_FOCUSED_NAV_IDS.filter((navId) => focusedEnabledNavIds.has(navId)),
  );
};

export const isFinancialAidAccessEnabledForApplication = (application: unknown): boolean => {
  const record = asRecord(application);
  if (record[FINANCIAL_AID_ACCESS_FIELD] === true) {
    return true;
  }
  if (record[FINANCIAL_AID_ACCESS_FIELD] === false) {
    return false;
  }

  const tuitionConfiguration = asRecord(record.tuitionConfiguration);
  return tuitionConfiguration[FINANCIAL_AID_ACCESS_FIELD] === true;
};

export const hasFinancialAidAccessForApplications = (applications: readonly unknown[]): boolean =>
  applications.some((application) => isFinancialAidAccessEnabledForApplication(application));

export const isFinancialAidAccessExplicitlyDeniedForApplication = (application: unknown): boolean => {
  const record = asRecord(application);
  if (record[FINANCIAL_AID_ACCESS_FIELD] === true) {
    return false;
  }
  if (
    record[FINANCIAL_AID_ACCESS_FIELD] === false
    && normalizeFinancialAidAccessDecision(record[FINANCIAL_AID_ACCESS_DECISION_FIELD])
      === FINANCIAL_AID_ACCESS_DECISION_REVOKED
  ) {
    return true;
  }

  const tuitionConfiguration = asRecord(record.tuitionConfiguration);
  return Boolean(
    tuitionConfiguration[FINANCIAL_AID_ACCESS_FIELD] === false
    && normalizeFinancialAidAccessDecision(tuitionConfiguration[FINANCIAL_AID_ACCESS_DECISION_FIELD])
      === FINANCIAL_AID_ACCESS_DECISION_REVOKED
  );
};

export const hasExplicitFinancialAidAccessDenialForApplications = (applications: readonly unknown[]): boolean =>
  applications.some((application) => isFinancialAidAccessExplicitlyDeniedForApplication(application));

export type FinancialAidAccessDecision = 'enabled' | 'denied' | null;

const normalizeFinancialAidAccessAcademicYear = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim().replace(/\s+/g, '');
  return /^\d{4}-\d{4}$/.test(normalized) ? normalized : null;
};

const normalizeFinancialAidAccessDecision = (value: unknown): 'enabled' | 'revoked' | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === FINANCIAL_AID_ACCESS_DECISION_ENABLED) return FINANCIAL_AID_ACCESS_DECISION_ENABLED;
  if (['revoked', 'denied', 'disabled'].includes(normalized)) return FINANCIAL_AID_ACCESS_DECISION_REVOKED;
  return null;
};

const hasFinancialAidAccessEventMetadata = (record: Record<string, unknown>): boolean =>
  Boolean(
    String(record.financialAidAccessUpdatedAt ?? '').trim()
      || String(record.financialAidAccessUpdatedBy ?? '').trim()
      || String(record.financialAidAccessUpdatedByRole ?? '').trim()
  );

const resolveApplicationFinancialAidAccessDecision = (application: unknown): FinancialAidAccessDecision => {
  const record = asRecord(application);
  if (record[FINANCIAL_AID_ACCESS_FIELD] === true) return 'enabled';
  if (
    record[FINANCIAL_AID_ACCESS_FIELD] === false
    && (
      normalizeFinancialAidAccessDecision(record[FINANCIAL_AID_ACCESS_DECISION_FIELD])
        === FINANCIAL_AID_ACCESS_DECISION_REVOKED
      || hasFinancialAidAccessEventMetadata(record)
    )
  ) {
    return 'denied';
  }

  const tuitionConfiguration = asRecord(record.tuitionConfiguration);
  if (tuitionConfiguration[FINANCIAL_AID_ACCESS_FIELD] === true) return 'enabled';
  if (
    tuitionConfiguration[FINANCIAL_AID_ACCESS_FIELD] === false
    && normalizeFinancialAidAccessDecision(tuitionConfiguration[FINANCIAL_AID_ACCESS_DECISION_FIELD])
      === FINANCIAL_AID_ACCESS_DECISION_REVOKED
  ) {
    return 'denied';
  }
  return null;
};

const hasExplicitFinancialAidAccessDecision = (application: unknown): boolean => {
  const record = asRecord(application);
  if (normalizeFinancialAidAccessDecision(record[FINANCIAL_AID_ACCESS_DECISION_FIELD])) {
    return true;
  }
  if (
    (record[FINANCIAL_AID_ACCESS_FIELD] === true || record[FINANCIAL_AID_ACCESS_FIELD] === false)
    && hasFinancialAidAccessEventMetadata(record)
  ) {
    return true;
  }
  const tuitionConfiguration = asRecord(record.tuitionConfiguration);
  return Boolean(normalizeFinancialAidAccessDecision(tuitionConfiguration[FINANCIAL_AID_ACCESS_DECISION_FIELD]));
};

const resolveAcademicYearRank = (academicYear: string | null): number => {
  if (!academicYear) return Number.NEGATIVE_INFINITY;
  const [startYear] = academicYear.split('-');
  const parsed = Number.parseInt(startYear, 10);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const parseTimestampCandidate = (candidate: unknown): number | null => {
  if (typeof candidate === 'number' && Number.isFinite(candidate)) {
    return candidate;
  }
  const normalized = String(candidate ?? '').trim();
  if (!normalized) {
    return null;
  }
  if (/^-?\d+(?:\.\d+)?$/.test(normalized)) {
    const parsedNumber = Number.parseFloat(normalized);
    if (Number.isFinite(parsedNumber)) {
      return parsedNumber;
    }
  }
  const parsedDate = Date.parse(normalized);
  return Number.isFinite(parsedDate) ? parsedDate : null;
};

const resolveTimestampRank = (application: unknown): number => {
  const record = asRecord(application);
  const candidates = [record.financialAidAccessUpdatedAt, record.updatedAt, record.createdAt];
  for (const candidate of candidates) {
    const parsed = parseTimestampCandidate(candidate);
    if (parsed !== null) {
      return parsed;
    }
  }
  return Number.NEGATIVE_INFINITY;
};

export const resolveFinancialAidAccessDecisionForApplications = (
  applications: readonly unknown[],
  options: { academicYear?: unknown; academicYears?: readonly unknown[] | null } = {},
): FinancialAidAccessDecision => {
  const targetAcademicYears = new Set(
    [
      options.academicYear,
      ...(options.academicYears ?? []),
    ]
      .map((value) => normalizeFinancialAidAccessAcademicYear(value))
      .filter((value): value is string => Boolean(value)),
  );
  const candidates = applications.flatMap((application, index) => {
    const decision = resolveApplicationFinancialAidAccessDecision(application);
    if (!decision) return [];
    const academicYear = normalizeFinancialAidAccessAcademicYear(asRecord(application).academicYear);
    if (targetAcademicYears.size > 0 && academicYear && !targetAcademicYears.has(academicYear)) {
      return [];
    }
    return [{
      decision,
      academicYearRank: resolveAcademicYearRank(academicYear),
      explicitDecisionRank: hasExplicitFinancialAidAccessDecision(application) ? 1 : 0,
      timestampRank: resolveTimestampRank(application),
      index,
    }];
  });
  candidates.sort((left, right) => (
    right.explicitDecisionRank - left.explicitDecisionRank
    || right.timestampRank - left.timestampRank
    || right.academicYearRank - left.academicYearRank
    || right.index - left.index
  ));
  return candidates[0]?.decision ?? null;
};

const isExistingFinancialAidApplicationRecord = (
  application: unknown,
  parentApplicantId?: string | null,
): boolean => {
  const record = asRecord(application);
  const id = String(record.id ?? '').trim();
  if (!id) {
    return false;
  }
  const requiredParentApplicantId = String(parentApplicantId ?? '').trim();
  if (requiredParentApplicantId && String(record.applicantId ?? '').trim() !== requiredParentApplicantId) {
    return false;
  }
  return Boolean(
    String(record.applicantId ?? '').trim()
      || String(record.academicYear ?? '').trim()
      || String(record.status ?? '').trim()
      || String(record.code ?? '').trim()
      || String(record.entrySource ?? '').trim(),
  );
};

export const hasFinancialAidAccessForExistingFinancialAidApplications = (
  applications: readonly unknown[],
  parentApplicantId?: string | null,
): boolean => applications.some((application) => (
  isExistingFinancialAidApplicationRecord(application, parentApplicantId)
));

export const hasFinancialAidInviteEntryParams = (search: string): boolean => {
  const normalized = String(search ?? '').trim();
  if (!normalized) return false;
  const params = new URLSearchParams(normalized.startsWith('?') ? normalized : `?${normalized}`);
  return Boolean(params.get('invite')?.trim() || params.get('academicYear')?.trim());
};

const resolveFinancialAidInviteEntryAcademicYear = (search: string): string | null => {
  const normalized = String(search ?? '').trim();
  if (!normalized) return null;
  const params = new URLSearchParams(normalized.startsWith('?') ? normalized : `?${normalized}`);
  return normalizeFinancialAidAccessAcademicYear(params.get('academicYear'));
};

export const isParentFinancialAidAccessRoute = (canonicalPath: string): boolean => {
  const normalizedPath = String(canonicalPath ?? '').trim().replace(/\/+$/, '') || '/';
  return PARENT_FINANCIAL_AID_ACCESS_ROUTE_PREFIXES.some((pathPrefix) => (
    normalizedPath === pathPrefix || normalizedPath.startsWith(`${pathPrefix}/`)
  ));
};

export type ParentFinancialAidAccessSource =
  | 'invite-entry'
  | 'admissions-application'
  | 'financial-aid-application'
  | 'open-cycle'
  | null;

export type ParentFinancialAidAccessGateStateInput = {
  canonicalPath: string;
  search: string;
  parentApplicantId?: string | null;
  admissionsApplications?: readonly unknown[] | null;
  admissionsLookupPending?: boolean | null;
  financialAidApplications?: readonly unknown[] | null;
  financialAidLookupPending?: boolean | null;
  // True when the school's configured Financial Aid cycle is currently open.
  // An open cycle admits any signed-in parent (general application links),
  // unless admissions explicitly denied Financial Aid for the family.
  cycleOpen?: boolean | null;
  openCycleEntryEnabled?: boolean | null;
  openCycleLookupPending?: boolean | null;
};

export type ParentFinancialAidAccessGateState = {
  isFinancialAidAccessRoute: boolean;
  hasInviteEntry: boolean;
  accessEnabled: boolean;
  accessSource: ParentFinancialAidAccessSource;
  lookupPending: boolean;
  gateActive: boolean;
};

export const resolveParentFinancialAidAccessGateState = (
  input: ParentFinancialAidAccessGateStateInput,
): ParentFinancialAidAccessGateState => {
  const isFinancialAidAccessRouteValue = isParentFinancialAidAccessRoute(input.canonicalPath);
  const hasInviteEntry = isFinancialAidAccessRouteValue && hasFinancialAidInviteEntryParams(input.search);
  const inviteEntryAcademicYear = resolveFinancialAidInviteEntryAcademicYear(input.search);
  const latestAdmissionsAccessDecision = resolveFinancialAidAccessDecisionForApplications(
    input.admissionsApplications ?? [],
  );
  const admissionsAccessDecision = resolveFinancialAidAccessDecisionForApplications(input.admissionsApplications ?? [], {
    academicYear: inviteEntryAcademicYear,
  });
  const admissionsAccessEnabled = admissionsAccessDecision === 'enabled';
  const admissionsAccessExplicitlyDenied = latestAdmissionsAccessDecision === 'denied';
  const existingFinancialAidAccessEnabled =
    !admissionsAccessExplicitlyDenied &&
    hasFinancialAidAccessForExistingFinancialAidApplications(
      input.financialAidApplications ?? [],
      input.parentApplicantId,
    );
  const openCycleAccessEnabled = Boolean(
    !admissionsAccessExplicitlyDenied
      && (input.cycleOpen || input.openCycleEntryEnabled),
  );
  const accessSource: ParentFinancialAidAccessSource = hasInviteEntry && !admissionsAccessExplicitlyDenied
    ? 'invite-entry'
    : admissionsAccessEnabled && !admissionsAccessExplicitlyDenied
      ? 'admissions-application'
      : existingFinancialAidAccessEnabled
        ? 'financial-aid-application'
        : openCycleAccessEnabled
          ? 'open-cycle'
          : null;
  const accessEnabled = Boolean(accessSource);
  const hasApplicantId = Boolean(String(input.parentApplicantId ?? '').trim());
  // An open cycle grants access immediately; record lookups only refine the
  // access source, so they must never hold parents at the gate.
  const lookupPending = Boolean(
    isFinancialAidAccessRouteValue
      && !hasInviteEntry
      && !openCycleAccessEnabled
      && hasApplicantId
      && !accessEnabled
      && (input.admissionsLookupPending || input.financialAidLookupPending || input.openCycleLookupPending),
  );

  return {
    isFinancialAidAccessRoute: isFinancialAidAccessRouteValue,
    hasInviteEntry,
    accessEnabled,
    accessSource,
    lookupPending,
    gateActive: Boolean(
      isFinancialAidAccessRouteValue
      && (!hasInviteEntry || admissionsAccessExplicitlyDenied)
      && !accessEnabled
    ),
  };
};
